
import React, { useState, useMemo, useEffect } from "react";
import api from "../../api/api";
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit  } from "lucide-react";
import Button from '../../components/ui/Button';

/* ---------------- ICONS ---------------- */
const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

/* ---------------- ITEMS LIST ---------------- */
const ItemsListPage = ({ items, onAddNew, onEditItem, isLoading, setView  }) => {
  const [viewMode, setViewMode] = useState("card");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          item.Item_desc1?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.item_no?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [items, searchQuery]
  );

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen text-xl">Loading Items...</div>;
  }

    return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <div className="px-3 py-3 bg-grey border-b">
        {/* Row 1: Title */}
        <div className="mb-3">
          <Heading>Item Master</Heading>
        </div>

        {/* Row 2: New + Grid/Table */}
        <div className="flex items-center gap-2 mb-3">
          {/* New Button */}
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
              rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
           onClick={onAddNew}
          >
            <PlusCircle size={18} />
            <span className="font-medium">New</span>
          </Button>

          <div className="flex-1" />
          {/* Grid / Table Toggle */}
          <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
            {/* Grid */}
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            {/* Table */}
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 3: Back + Search Box */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("home")}
          >
            ⬅
          </Button>

          <SearchInput
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by item name or number..."
            />
        </div>
      </div>


      {/* Card or Table */}
      {viewMode === "card" ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          {filteredItems.map((item) => (
            <div
              key={item.itemid}
              onClick={() => onEditItem(item)}
              className="bg-white shadow-md cursor-pointer rounded-xl hover:shadow-xl group"
            >
              <img
                src={item.filepath || `https://placehold.co/400x400?text=${item.item_no}`}
                alt={item.Item_desc1}
                className="object-cover w-full h-48"
              />
              <div className="p-4">
                <h3 className="font-semibold truncate group-hover:text-blue-600">
                  {item.Item_desc1}
                </h3>
                <p className="text-sm text-gray-500">{item.item_no}</p>
                <p className="mt-2 text-lg font-bold">₹{item.standardcost}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 shadow-md rounded-xl">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="tracking-wider font-bold text-gray-900 uppercase bg-gray-100">
              <tr>
                <th className="p-4 font-semibold">Item No</th>
                <th className="p-4 font-semibold">Description</th>
                <th className="p-4 font-semibold text-right">Standard Cost</th>
                <th className="p-4 font-semibold text-right">MRP</th>
                <th className="p-4 font-semibold text-right">Stock</th>
                <th className="p-4 font-semibold text-center">Image</th>
                <th className="p-4 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <tr key={item.itemid} className="transition-colors duration-150 hover:bg-gray-50">
                    <td className="p-4">{item.item_no}</td>
                    <td className="max-w-xs p-4 truncate">{item.Item_desc1}</td>
                    <td className="p-4 text-right">₹{item.standardcost.toLocaleString("en-IN")}</td>
                    <td className="p-4 text-right">₹{item.mrp.toLocaleString("en-IN")}</td>
                    <td className="p-4 text-right">{item.StkQty.toLocaleString("en-IN")}</td>
                    <td className="p-4 text-center">
                      {item.filepath ? (
                        <img src={item.filepath} alt="Item" className="object-cover w-12 h-12 border rounded-md" />
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => onEditItem(item)}
                        className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                      >
                         <FileEdit className="w-5 h-5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-gray-500">No items found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------------- ITEM FORM ---------------- */
const ItemForm = ({ initialData, onCancel, onSave, taxGroups, action }) => {
  const [formData, setFormData] = useState(initialData || {});

  useEffect(() => {
    setFormData(initialData || {});
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData, action);
  };

  return (
    <div className="max-w-2xl p-6 mx-auto bg-white shadow rounded-xl">
      <h2 className="mb-4 text-xl font-bold text-center sm:text-2xl">
        {action === "insert" ? "Add New Item" : "Edit Item"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {[
          { label: "Item No", name: "item_no" },
          { label: "Description", name: "Item_desc1" },
          { label: "Item Type", name: "item_type" },
          { label: "Make", name: "Make" },
          { label: "UOM", name: "uom" },
          { label: "Standard Cost", name: "standardcost", type: "number" },
          { label: "MRP", name: "mrp", type: "number" },
          { label: "Stock Quantity", name: "StkQty", type: "number" },
          { label: "Image URL", name: "filepath" }
        ].map(({ label, name, type }) => (
          <div key={name}>
            <label className="block mb-1 font-medium">{label}</label>
            <input
              name={name}
              type={type || "text"}
              value={formData[name] || ""}
              onChange={handleChange}
              placeholder={label}
              className="w-full p-2 border rounded"
            />
          </div>
        ))}

        <div>
          <label className="block mb-1 font-medium">HSN Code</label>
          <input
            name="hsncode"
            type="text"
            value={formData.hsncode || ""}
            onChange={handleChange}
            placeholder="HSN Code"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Tax Group</label>
          <select
            name="taxgroupid"
            value={formData.taxgroupid || ""}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          >
            <option value="">-- Select Tax Group --</option>
            {taxGroups.map((group) => (
              <option key={group.groupid} value={group.groupid}>
                {group.groupName}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3">
          <button type="submit" className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
            {action === "insert" ? "Save" : "Update"}
          </button>
          <button type="button" onClick={onCancel} className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------------- MAIN ITEM MASTER ---------------- */
export default function ItemMaster({ setView }) {
  const [currentPage, setCurrentPage] = useState("list");
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [taxGroups, setTaxGroups] = useState([]);

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const response = await api.getItemList("UsrName");
      const apiItems = response?.Table || [];
      setItems(
        apiItems.map((item) => ({
          itemid: item.itemid,
          item_no: item.item_no,
          Item_desc1: item.Item_desc1 || "",
          standardcost: Number(item.standardcost || 0),
          mrp: Number(item.mrp || 0),
          StkQty: Number(item.StkQty || 0),
          filepath: item.filepath || "",
          item_type: item.item_type || "",
          Make: item.Make || "",
          uom: item.uom || "",
          hsncode: item.hsncode || "",
          taxgroupid: item.taxgroupid || null,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaxGroups = async () => {
    try {
      const res = await api.getItemTaxAPI("admin");
      setTaxGroups(res?.Table || []);
    } catch (err) {
      console.error("Failed to fetch tax groups:", err);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchTaxGroups();
  }, []);

  const handleAddNew = () => {
    setEditingItem(null);
    setCurrentPage("add");
  };

  const handleEditItem = async (item) => {
    try {
      setIsLoading(true);

      // Ensure taxGroups loaded
      let loadedGroups = taxGroups;
      if (taxGroups.length === 0) {
        const resTax = await api.getItemTaxAPI("admin");
        loadedGroups = resTax?.Table || [];
        setTaxGroups(loadedGroups);
      }

      const res = await api.EditItemList(item.itemid);
      const apiItem = res?.Table?.[0];

      if (apiItem) {
        const formatted = {
          itemid: apiItem.Itemid,
          item_no: apiItem.Item_No || "",
          Item_desc1: apiItem.Item_desc1 || "",
          item_type: apiItem.Item_Type || "",
          Make: apiItem.Make || "N/A",
          uom: apiItem.Uom || "",
          standardcost: Number(apiItem.Standardcost || 0),
          mrp: Number(apiItem.Mrp || 0),
          filepath: apiItem.filepath || "",
          hsncode: apiItem.HsnCode || "",
          taxgroupid:
            loadedGroups.find((g) => g.groupName === apiItem.taxtype)?.groupid || "",
        };
        setEditingItem(formatted);
        setCurrentPage("edit");
      }
    } catch (err) {
      console.error("Failed to fetch item details for edit:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveItem = async (itemData, action) => {
    try {
      const selectedGroup = taxGroups.find(
        (g) => Number(g.groupid) === Number(itemData.taxgroupid)
      );

      const payload = {
        Sqlaction: action,
        ItemId: action === "update" ? itemData.itemid : 0,
        Itemno: itemData.item_no || "",
        Itemdesc: itemData.Item_desc1 || "",
        ItemType: itemData.item_type || "-",
        Make: itemData.Make || "N/A",
        Uom: itemData.uom || "-",
        Standardcost: Number(itemData.standardcost || 0),
        Mrp: Number(itemData.mrp || 0),
        Filepath: itemData.filepath || "-",
        taxtype: selectedGroup ? selectedGroup.groupName : "",
        HsnCode: itemData.hsncode || "",
      };

      console.log("📤 Final Save payload:", payload);

      const response = await api.SaveItemList(payload);
      console.log("✅ Save API Response:", response);

      await fetchItems();
      setCurrentPage("list");
    } catch (err) {
      console.error("❌ Save failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {currentPage === "list" && (
        <ItemsListPage
          items={items}
          onAddNew={handleAddNew}
          onEditItem={handleEditItem}
          isLoading={isLoading}
           setView={setView} 
        />
      )}

      {currentPage === "add" && (
        <ItemForm
          onCancel={() => setCurrentPage("list")}
          onSave={handleSaveItem}
          taxGroups={taxGroups}
          action="insert"
        />
      )}

      {currentPage === "edit" &&
        (editingItem ? (
          <ItemForm
            initialData={editingItem}
            onCancel={() => setCurrentPage("list")}
            onSave={handleSaveItem}
            taxGroups={taxGroups}
            action="update"
          />
        ) : (
          <div className="text-center text-gray-600 p-4">
            Loading item details...
          </div>
        ))}
    </div>
  );
}



